import cn from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'

import { GlobalPortalLoader } from 'routes/portal/components/GlobalPortalLoader'
import { t } from 'services/Translation'

import * as s from './PlayerLoadingOverlay.module.scss'
import {
  FADE_OUT_MS,
  HARD_TIMEOUT_MS,
  IOverlayInputs,
  nextPhase,
  OverlayPhase,
} from './state-machine'

// Interval between reducer re-evaluations while the overlay is visible.
// The reducer is pure so re-running it 10×/s while we wait on a postMessage
// is cheap — the alternative is scheduling individual setTimeouts per
// timeout-bearing phase, which doubles the state bookkeeping.
const REDUCER_TICK_MS = 100

export interface IPlayerLoadingOverlayProps {
  queryLoading: boolean
  sessionCreating: boolean
  sessionReady: boolean
  launcherSessionStatus: 'initialized' | 'terminated' | null
  launcherCourseReady: boolean
  iframeLoaded: boolean
  iframeErrored: boolean
  onPhaseChange?: (phase: OverlayPhase) => void
  onBackToPortal?: () => void
}

// Drives overlay phase via `nextPhase` reducer + a timer tick. Timer tracks
// both phase-entry timestamps (for SESSION_INIT/COURSE_READY fallbacks and
// IFRAME_LOAD timeout) and mount timestamp (for HARD_TIMEOUT_MS ceiling).
const usePlayerOverlayPhase = (
  inputs: Omit<
    IOverlayInputs,
    'elapsedInIframeLoad' | 'elapsedInSessionInit' | 'elapsedInCourseReady' | 'elapsedTotal'
  >,
): OverlayPhase => {
  const [phase, setPhase] = useState<OverlayPhase>('idle')

  const mountedAtRef = useRef<number>(performance.now())
  const phaseEnteredAtRef = useRef<Map<OverlayPhase, number>>(new Map())
  const inputsRef = useRef(inputs)
  inputsRef.current = inputs

  // Track phase-entry timestamps. `idle` is entered at mount; subsequent
  // phases get timestamp when we transition into them.
  useEffect(() => {
    if (!phaseEnteredAtRef.current.has(phase)) {
      phaseEnteredAtRef.current.set(phase, performance.now())
    }
  }, [phase])

  useEffect(() => {
    const tick = () => {
      const now = performance.now()
      const elapsedTotal = now - mountedAtRef.current
      const elapsedIn = (p: OverlayPhase): number => {
        const t = phaseEnteredAtRef.current.get(p)
        return t === undefined ? -1 : now - t
      }

      const fullInputs: IOverlayInputs = {
        ...inputsRef.current,
        elapsedInIframeLoad: elapsedIn('iframeLoad'),
        elapsedInSessionInit: elapsedIn('sessionInit'),
        elapsedInCourseReady: elapsedIn('courseReady'),
        elapsedTotal,
      }

      setPhase((current) => {
        const next = nextPhase(current, fullInputs)
        if (next !== current && !phaseEnteredAtRef.current.has(next)) {
          phaseEnteredAtRef.current.set(next, now)
        }
        return next
      })
    }

    // Immediate first tick — advances idle → query on mount synchronously.
    tick()
    const id = window.setInterval(tick, REDUCER_TICK_MS)
    // Hard cleanup ceiling — in case interval somehow keeps running past
    // HARD_TIMEOUT_MS without the reducer collapsing to done.
    const hardStop = window.setTimeout(
      () => {
        window.clearInterval(id)
      },
      HARD_TIMEOUT_MS + FADE_OUT_MS + 100,
    )
    return () => {
      window.clearInterval(id)
      window.clearTimeout(hardStop)
    }
  }, [])

  return phase
}

// Visual collapsed into backend-injected boot-loader. Normal loading phases
// render nothing (the branded boot-loader in index.html already covers the
// screen until we hide it). On `done` we hide the boot-loader so the course
// iframe becomes visible. On `error` we also hide the boot-loader and render
// a full-screen error state in its place (since the boot-loader has no
// interactivity).
const PlayerLoadingOverlay: FC<IPlayerLoadingOverlayProps> = ({
  queryLoading,
  sessionCreating,
  sessionReady,
  launcherSessionStatus,
  launcherCourseReady,
  iframeLoaded,
  iframeErrored,
  onPhaseChange,
  onBackToPortal,
}) => {
  const phase = usePlayerOverlayPhase({
    queryLoading,
    sessionCreating,
    sessionReady,
    launcherSessionStatus,
    launcherCourseReady,
    iframeLoaded,
    iframeErrored,
  })

  const onReload = () => {
    window.location.reload()
  }

  useEffect(() => {
    onPhaseChange?.(phase)
  }, [phase, onPhaseChange])

  // Boot-loader is held visible while the state machine is still working;
  // released as soon as it settles into `done` or `error`. On `error` the
  // boot-loader closes and we render a full-screen error UI in its place.
  const isLoading = phase !== 'done' && phase !== 'error'

  return (
    <>
      <GlobalPortalLoader reason='lms-player' visible={isLoading} />
      {phase === 'error' && (
        <div aria-live='assertive' className={cn(s.root, s.errorRoot)} role='alert'>
          <h2 className={s.errorTitle}>{t('contentSpace.public.overlay.errorTitle')}</h2>
          <div className={s.actions}>
            <button className={cn(s.button, s.buttonPrimary)} onClick={onReload} type='button'>
              {t('contentSpace.public.overlay.reload')}
            </button>
            {onBackToPortal && (
              <button className={s.button} onClick={onBackToPortal} type='button'>
                {t('contentSpace.public.overlay.backToPortal')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default PlayerLoadingOverlay
export { usePlayerOverlayPhase }
export type { OverlayPhase }
